import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { FolderIcon, CodeIcon, LayoutIcon, UserIcon, MessageCircleIcon, SettingsIcon } from 'lucide-react';
import { useWindows } from '../../contexts/WindowContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getGradientClasses } from '../../utils/helpers';
import { CustomIcon } from '../ui/CustomIcon';
// Reorganized with clear hierarchy and simplified structure
const desktopAppGroups = [{
  id: 'primary',
  apps: [{
    id: 'portfolio',
    name: 'Portfolio',
    icon: FolderIcon,
    color: 'from-blue-500 to-indigo-500',
    pattern: 'square-rotate'
  }, {
    id: 'expertise',
    name: 'Expertise',
    icon: CodeIcon,
    color: 'from-purple-500 to-violet-500',
    pattern: 'circles'
  }, {
    id: 'process',
    name: 'Werkwijze',
    icon: LayoutIcon,
    color: 'from-pink-500 to-rose-500',
    pattern: 'triangles'
  }]
}, {
  id: 'secondary',
  apps: [{
    id: 'about',
    name: 'About',
    icon: UserIcon,
    color: 'from-emerald-500 to-teal-500',
    pattern: 'dots'
  }, {
    id: 'contact',
    name: 'Contact',
    icon: MessageCircleIcon,
    color: 'from-amber-500 to-orange-500',
    pattern: 'diamond'
  }]
}, {
  id: 'utility',
  apps: [{
    id: 'settings',
    name: 'Settings',
    icon: SettingsIcon,
    color: 'from-slate-500 to-gray-500',
    pattern: 'grid'
  }]
}];
// Mobile apps - simplified
const mobileApps = [{
  id: 'about',
  name: 'About',
  icon: UserIcon,
  color: 'from-emerald-500 to-teal-500',
  pattern: 'dots'
}, {
  id: 'expertise',
  name: 'Expertise',
  icon: CodeIcon,
  color: 'from-purple-500 to-violet-500',
  pattern: 'circles'
}, {
  id: 'portfolio',
  name: 'Portfolio',
  icon: FolderIcon,
  color: 'from-blue-500 to-indigo-500',
  pattern: 'square-rotate'
}, {
  id: 'process',
  name: 'Werkwijze',
  icon: LayoutIcon,
  color: 'from-pink-500 to-rose-500',
  pattern: 'triangles'
}, {
  id: 'contact',
  name: 'Contact',
  icon: MessageCircleIcon,
  color: 'from-amber-500 to-orange-500',
  pattern: 'diamond'
}, {
  id: 'settings',
  name: 'Settings',
  icon: SettingsIcon,
  color: 'from-slate-500 to-gray-500',
  pattern: 'grid'
}];
export function Dock() {
  const {
    openWindow,
    windows,
    isMobile,
    minimizeWindow
  } = useWindows();
  const {
    t,
    getThemeClasses,
    theme
  } = useTheme();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const getAppName = (appId: string) => {
    switch (appId) {
      case 'about':
        return 'About';
      case 'expertise':
        return t.apps.expertise || 'Expertise';
      case 'portfolio':
        return t.apps.portfolio || 'Portfolio';
      case 'process':
        return t.apps.process || 'Werkwijze';
      case 'contact':
        return t.apps.contact || 'Contact';
      case 'settings':
        return t.apps.settings || 'Settings';
      case 'ai-assistant':
        return t.apps.aiAssistant || 'AI Assistant';
      default:
        return appId;
    }
  };
  const handleAppClick = (appId: string) => {
    const existingWindow = windows.find(w => w.appId === appId);
    if (existingWindow) {
      // If window is open and active (not minimized), minimize it
      if (!existingWindow.isMinimized) {
        // Check if it's the top window
        const isTopWindow = windows.every(w => w.zIndex <= existingWindow.zIndex);
        if (isTopWindow) {
          minimizeWindow(existingWindow.id);
          return;
        }
      }
      // If window exists and is minimized, restore it
      if (existingWindow.isMinimized) {
        minimizeWindow(existingWindow.id);
      } else {
        // If window exists and is open but not top, focus it
        openWindow(appId, getAppName(appId));
      }
    } else {
      // If window doesn't exist, open it
      openWindow(appId, getAppName(appId));
    }
  };
  // Mobile dock
  if (isMobile) {
    return <motion.nav initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.2,
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed bottom-0 left-0 right-0 z-50 pb-safe" role="navigation" aria-label="Main application navigation" data-id="element-767">
        <div className={`${themeClasses.dock} border-t ${themeClasses.border} px-3 py-3 backdrop-blur-2xl`} data-id="element-768">
          <div className="flex items-center justify-between max-w-md mx-auto" data-id="element-769">
            {mobileApps.map(app => {
            const isOpen = windows.some(w => w.appId === app.id && !w.isMinimized);
            return <button key={app.id} onClick={() => handleAppClick(app.id)} className="flex flex-col items-center space-y-1.5 py-1 relative group" aria-label={`${isOpen ? 'Switch to' : 'Open'} ${getAppName(app.id)}`} aria-current={isOpen ? 'page' : undefined} data-id="element-770">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all relative overflow-hidden
                      ${isOpen ? `bg-gradient-to-br ${app.color} shadow-lg` : `${themeClasses.card} border ${themeClasses.border}`}`} data-id="element-771">
                    <CustomIcon type={app.id as any} className={`w-5 h-5 ${isOpen ? 'text-white' : themeClasses.icon}`} data-id="element-772" />
                  </div>
                  <span className={`${themeClasses.text} text-[10px] font-medium truncate w-full text-center`} data-id="element-773">
                    {getAppName(app.id)}
                  </span>
                  {isOpen && <motion.div layoutId={`mobile-indicator-${app.id}`} className={`absolute -bottom-1 w-1 h-1 rounded-full bg-gradient-to-r ${app.color}`} data-id="element-774" />}
                </button>;
          })}
          </div>
        </div>
      </motion.nav>;
  }
  // Desktop dock - refined styling
  return <motion.nav initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    delay: 0.2,
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50" role="navigation" aria-label="Main application navigation" data-id="element-775">
      <div className={`${themeClasses.dock} border ${themeClasses.border} rounded-2xl px-3 py-3 flex items-center gap-2 backdrop-blur-2xl ${theme === 'neon' ? 'shadow-xl shadow-teal-500/10' : theme === 'light' ? 'shadow-xl shadow-indigo-500/10' : 'shadow-xl shadow-violet-500/10'}`} data-id="element-776">
        {desktopAppGroups.map((group, groupIndex) => <Fragment key={group.id} data-id="element-777">
            <div className="flex items-center space-x-2" role="group" aria-label={`${group.id} applications`} data-id="element-778">
              {group.apps.map(app => {
            const isOpen = windows.some(w => w.appId === app.id && !w.isMinimized);
            return <motion.button key={app.id} whileHover={{
              scale: 1.15,
              y: -6
            }} whileTap={{
              scale: 0.95
            }} onClick={() => handleAppClick(app.id)} className="relative group" aria-label={`${isOpen ? 'Switch to' : 'Open'} ${getAppName(app.id)}`} aria-current={isOpen ? 'page' : undefined} data-id="element-779">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all relative overflow-hidden shadow-sm
                        ${isOpen ? `bg-gradient-to-br ${app.color}` : `${themeClasses.card} border ${themeClasses.border}`}`} data-id="element-780">
                      <CustomIcon type={app.id as any} className={`w-6 h-6 ${isOpen ? 'text-white' : themeClasses.icon} transition-colors`} data-id="element-781" />
                      {!isOpen && <motion.div className={`absolute inset-0 bg-gradient-to-br ${app.color} opacity-0`} initial={{
                  opacity: 0
                }} whileHover={{
                  opacity: 0.1
                }} transition={{
                  duration: 0.2
                }} data-id="element-782" />}
                    </div>
                    {isOpen && <motion.div layoutId={`indicator-${app.id}`} className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-gradient-to-r ${app.color} rounded-full`} aria-hidden="true" data-id="element-783" />}
                    <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 8
              }} whileHover={{
                opacity: 1,
                scale: 1,
                y: 0
              }} transition={{
                duration: 0.15
              }} className={`absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 ${themeClasses.tooltip} text-xs font-medium rounded-lg pointer-events-none whitespace-nowrap backdrop-blur-xl shadow-lg`} role="tooltip" data-id="element-784">
                      {getAppName(app.id)}
                    </motion.div>
                  </motion.button>;
          })}
            </div>
            {groupIndex < desktopAppGroups.length - 1 && <div className={`h-6 w-px ${themeClasses.border} mx-1`} aria-hidden="true" data-id="element-785" />}
          </Fragment>)}
      </div>
    </motion.nav>;
}